import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { LoadingSkeleton, ModalUI, Switch, useQueryFetch } from 'sonata-react-components';

import { getChangeLog } from '@/resources/changeLog.resources';
import ChangelogChanges from './ChangelogChanges';
import { ChangelogModalStyled } from './ChangelogModal.styles';

import type { ChangeLogData, MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';

type State = {
  items: ChangeLogData[];
};

type Props = {
  entity: MultiFlightEntityType;
  entityId: string;
  onCloseModalCallback: () => void;
  title: string;
  userId: string;
};

function ChangelogModal({ entity, entityId, onCloseModalCallback, title, userId }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const modalRef: any = useRef();

  useEffect(() => {
    modalRef?.current?.openModal();
    return () => modalRef?.current?.closeModal();
  }, []);

  const [{ items }, setState] = useState<State>({
    items: []
  });

  const { isFetching, isLoading } = useQueryFetch(
    ['getChangelog', entity, entityId, userId],
    async () => {
      const resp = await getChangeLog(entity, entityId, userId, {});
      setState(prev => ({
        ...prev,
        items: resp ?? []
      }));
    },
    {
      enabled: true,
      staleTime: 0
    }
  );

  return (
    <ModalUI
      closeModalCallback={onCloseModalCallback}
      ref={modalRef}
      title={`${formatMessage({
        defaultMessage: 'Change Log:',
        id: 'changelogModal.title'
      })} ${title}`}
    >
      <ChangelogModalStyled className="changelogModal" data-testid="changelogModal">
        <Switch>
          <Switch.Case condition={!!items.length}>
            <table className="changelogModal__table">
              <thead>
                <tr>
                  <th className="changelogModal__tableTh">
                    {formatMessage({
                      defaultMessage: 'Date',
                      id: 'changelogModal.columns.date'
                    })}
                  </th>
                  <th className="changelogModal__tableTh">
                    {formatMessage({
                      defaultMessage: 'User',
                      id: 'changelogModal.columns.user'
                    })}
                  </th>
                  <th className="changelogModal__tableTh">
                    {formatMessage({
                      defaultMessage: 'Changes',
                      id: 'changelogModal.columns.changes'
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item: ChangeLogData, key: number) => {
                  const date = moment(item.timestamp).utc().format('YYYY-MM-DD h:mm');
                  // @ts-expect-error
                  const name = item?.user?.name;
                  return (
                    <tr className="changelogModal__tableTr" key={key}>
                      <td className="changelogModal__tableTd">{date}</td>
                      <td className="changelogModal__tableTd">{name}</td>
                      <td className="changelogModal__tableTd">
                        <ChangelogChanges changeType={item.type} changes={item.changes} entity={entity} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Switch.Case>
          <Switch.Case condition={!items.length && !isLoading && !isFetching}>
            <div className="changelogModal__emptyText">
              <Switch>
                <Switch.Case condition={entity === 'insertionOrder'}>
                  {formatMessage({
                    defaultMessage: 'There are no changes in this Insertion Order.',
                    id: 'changelogModal.emptyIO'
                  })}
                </Switch.Case>
                <Switch.Case condition={entity === 'campaignPlan'}>
                  {formatMessage({
                    defaultMessage: 'There are no changes in this Campaign Plan.',
                    id: 'changelogModal.emptyCP'
                  })}
                </Switch.Case>
                <Switch.Default>
                  {formatMessage({
                    defaultMessage: 'There are no changes in this Line Item.',
                    id: 'changelogModal.emptyLI'
                  })}
                </Switch.Default>
              </Switch>
            </div>
          </Switch.Case>
          <Switch.Case condition={isFetching || isLoading}>
            <div className="changelogModal__skeleton">
              <LoadingSkeleton count={5} height={30} width="100%" />
            </div>
          </Switch.Case>
        </Switch>
      </ChangelogModalStyled>
    </ModalUI>
  );
}

export default ChangelogModal;
